import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { computeLightingOverallPct } from '../utils/progress';
import { estimatePageOnlySeconds, estimateReceptacleSeconds } from '../utils/timeEstimate';

export type CountSingleComponentOptions = {
  keepCountingState?: boolean;
  lightingGroupComponentsOverride?: any[];
  teleDataGroupComponentsOverride?: any[];
  countTargetNamesOverride?: Set<string>;
  timerPageCountOverride?: number;
};

export interface UseCountSingleComponentParams {
  pdfDocument: any;
  numPages: number | null;
  pageNumber: number;
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  file: { url?: string } | null;
  lightingGroupComponents: any[];
  teleDataGroupComponents: any[];
  electricalKey: any;
  streamRunTokensRef: MutableRefObject<Map<number, string>>;
  retryPayloadsRef: MutableRefObject<Map<number, {
    component: any;
    componentIndex: number;
    pagesToCount: number[];
    options?: CountSingleComponentOptions;
  }>>;
  countTargetNamesRef: MutableRefObject<Set<string>>;
  setCountAllComponentProgress: (fn: (prev: any) => any) => void;
  setMultiComponentDetections: (fn: (prev: any) => any) => void;
  setAllComponentSelections: (fn: (prev: any) => any) => void;
  setCountAllProgress: (v: string) => void;
  setIsCountingAll: (v: boolean) => void;
  setDetectionClassifications: (fn: (prev: any) => any) => void;
  setLightingProgress: (fn: (prev: any) => any) => void;
}

export function useCountSingleComponent(params: UseCountSingleComponentParams) {
  const {
    pdfDocument,
    numPages,
    pageNumber,
    canvasRef,
    file,
    lightingGroupComponents,
    teleDataGroupComponents,
    electricalKey,
    streamRunTokensRef,
    retryPayloadsRef,
    countTargetNamesRef,
    setCountAllComponentProgress,
    setMultiComponentDetections,
    setAllComponentSelections,
    setCountAllProgress,
    setIsCountingAll,
    setDetectionClassifications,
    setLightingProgress,
  } = params;

  const countSingleComponent = useCallback(async (
    component: any,
    componentIndex: number,
    pagesToCount?: number[],
    options?: CountSingleComponentOptions
  ): Promise<void> => {
    if (!pdfDocument || !numPages) return;
    const shouldClearGlobalProgress = !options?.keepCountingState;
    const resolvedLightingGroupComponents = options?.lightingGroupComponentsOverride ?? lightingGroupComponents;
    const resolvedTeleDataGroupComponents = options?.teleDataGroupComponentsOverride ?? teleDataGroupComponents;
    const resolvedTargetNames = options?.countTargetNamesOverride ?? countTargetNamesRef.current;
    const finalizeComponentCount = () => {
      if (!shouldClearGlobalProgress) return;
      setIsCountingAll(false);
      setCountAllProgress('');
    };

    const runToken = crypto.randomUUID();
    streamRunTokensRef.current.set(componentIndex, runToken);
    retryPayloadsRef.current.set(componentIndex, { component, componentIndex, pagesToCount: pagesToCount ?? Array.from({ length: numPages }, (_, i) => i + 1), options });

    const markComponentFailed = (message: string) => {
      setCountAllComponentProgress(prev => prev.map((p: any, i: number) =>
        i === componentIndex ? { ...p, status: 'failed' as const, failMessage: message } : p
      ));
      setMultiComponentDetections(prev => prev.map((d: any, idx: number) =>
        idx === componentIndex ? { ...d, detections: [] } : d
      ));
      setAllComponentSelections(prev => {
        const newMap = new Map(prev);
        const compKey = component.name;
        newMap.set(compKey, new Set<string>());
        return newMap;
      });
    };

    const pageNumbers = pagesToCount ?? Array.from({ length: numPages }, (_, i) => i + 1);
    const pageCount = pageNumbers.length;
    const estimatePageCount = Math.max(0, Math.floor(options?.timerPageCountOverride ?? pageCount));
    let detectionsForEstimate = 0;
    const componentNameLower = (component.name || '').toLowerCase();
    const isReceptacleComponent =
      componentNameLower.includes('receptacle') ||
      componentNameLower.includes('duplex') ||
      (component as any).componentType === 'Receptacles';
    const isReceptaclesGroup = component.name === '__RECEPTACLES_GROUP__';
    const usesReceptacleDetectionEstimate = isReceptacleComponent || isReceptaclesGroup;

    const reviseEstimateIfNeeded = (detectionCount: number) => {
      const modeledEstimate = usesReceptacleDetectionEstimate
        ? estimateReceptacleSeconds(estimatePageCount, detectionCount)
        : estimatePageOnlySeconds(estimatePageCount);
      setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
        i === componentIndex
          ? { ...p, estimatedTotalSeconds: Math.max(p.estimatedTotalSeconds || 0, modeledEstimate) }
          : p
      ));
    };
    const isAcceptableStreamCompletion = (
      receivedDoneSignal: boolean,
      completedPages: number,
      expectedPages: number,
      streamName: string
    ): boolean => {
      if (expectedPages <= 0) return true;
      if (completedPages >= expectedPages) return true;
      if (!receivedDoneSignal) return false;

      const completionRatio = completedPages / expectedPages;
      const MOSTLY_COMPLETE_RATIO = 0.8;
      const isMostlyComplete = completionRatio >= MOSTLY_COMPLETE_RATIO;
      if (isMostlyComplete) {
        console.warn(
          `⚠️ ${streamName} stream ended with partial page-done coverage (${completedPages}/${expectedPages}). ` +
          `Accepting run because ${(completionRatio * 100).toFixed(1)}% >= ${(MOSTLY_COMPLETE_RATIO * 100).toFixed(0)}%.`
        );
      }
      return isMostlyComplete;
    };
    const maxNoResultRetries = 2;
    // Power-device counting now runs a single attempt (no auto-retry).
    const maxPowerNoResultRetries = 0;
    const maxPowerTrafficRetries = 0;
    const noResultMessagePattern = /\b(?:no\s+(?:valid\s+)?(?:fixture\s*codes?|fixtures?|fixutres?|detections?|matches?)(?:\s+(?:found|after\s+normalization))?|none\s+found|nothing\s+found)\b/i;
    const isNoResultMessage = (value: unknown): boolean => {
      const normalized = String(value ?? '').trim();
      return normalized.length > 0 && noResultMessagePattern.test(normalized);
    };

    console.log(`🔍 Counting "${component.name}" across ${pageCount} pages (concurrent)...`);
    setCountAllProgress(`Counting "${component.name}" across ${pageCount} pages...`);

    const isLightingComponent = component.symbolType === 'Lighting';
    const isLightingGroup = component.name === '__LIGHTING_GROUP__';
    const isSwitchComponent = (component as any).componentType === 'Switches' || (componentNameLower.includes('switch') && !isReceptacleComponent);
    const isTeleDataGroup = component.name === '__TELE_DATA_GROUP__';
    const isTeleDataComponent = isTeleDataGroup || component.name.toLowerCase().includes('tel') || component.name.toLowerCase().includes('data') || component.name.toLowerCase().includes('communication') || (component as any).componentType === 'Telecom' || (component as any).componentType === 'Low Voltage';
    const isJunctionBoxComponent = (() => {
      const n = (component.name || '').toLowerCase().replace(/[-_]/g, ' ');
      return n.includes('junction') || n.includes('jbox') || n.includes('j box') || (n.includes('j') && n.includes('box'));
    })();

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Receptacles use fully server-side processing.
    // The server downloads the PDF, renders the page at 16,000px using pdfjs-dist +
    // @napi-rs/canvas, tiles with sharp, runs Roboflow, and streams back detections.
    // No browser rendering or Supabase upload needed. Pages process 3 at a time.
    // ============================================================
    if ((isReceptacleComponent || isReceptaclesGroup) && file?.url) {
      const displayName = isReceptaclesGroup ? 'RECEPTACLES' : component.name;
      console.log(`🔌 CLOUD RUN: Counting "${displayName}" on pages [${pageNumbers.join(', ')}] — single request, PDF downloaded once`);
      setCountAllProgress(`Counting "${displayName}" across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      const receptacleRefTypes = electricalKey?.components
        ? electricalKey.components
            .filter((c: any) =>
              c.name.toLowerCase().includes('receptacle') ||
              c.componentType === 'Receptacles' ||
              c.componentType === 'Power'
            )
            .map((c: any, idx: number) => ({
              index: idx,
              name: c.name,
              symbol: c.symbol || '',
              referenceImage: c.referenceImage || ''
            }))
        : [];
      const shouldClassifyAll = isReceptaclesGroup && receptacleRefTypes.length > 1;

      for (let _attempt = 0; _attempt < maxPowerNoResultRetries + 1; _attempt++) {
      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;
        let receivedDone = false;
        const attemptToken = _attempt === 0 ? runToken : crypto.randomUUID();
        streamRunTokensRef.current.set(componentIndex, attemptToken);

        const response = await fetch('https://joules--receptacle-multipage-pipeline-process.modal.run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            componentType: isReceptaclesGroup ? 'receptacle' : component.name,
            confidence: 0.01,
            referenceTypes: (shouldClassifyAll || receptacleRefTypes.length > 1) ? receptacleRefTypes : undefined,
            targetComponentName: shouldClassifyAll ? undefined : (receptacleRefTypes.length > 1 ? component.name : undefined)
          })
        });

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            if (streamRunTokensRef.current.get(componentIndex) !== attemptToken) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  const nextPct = Math.max(0, Math.min(100, Number(msg.pct) || 0));
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex
                      ? { ...p, completedBatches: Math.max(p.completedBatches || 0, nextPct), totalBatches: 100 }
                      : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `unfiltered-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                      unfiltered: true
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);
                  detectionsForEstimate += newDetections.length;
                  reviseEstimateIfNeeded(detectionsForEstimate);

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                    newDetections.forEach((d: any) => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} unfiltered detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'classify' && isReceptaclesGroup) {
                const detId = `unfiltered-${msg.pageNum}-${msg.relX.toFixed(5)}-${msg.relY.toFixed(5)}`;
                const det = allDetections.find((d: any) => d.id === detId);
                if (det) {
                  det.prediction.classifiedName = msg.classifiedName;
                  det.prediction.classifiedIndex = msg.classifiedIndex;
                  det.prediction.unfiltered = false;
                  setDetectionClassifications((prev: any) => {
                    const newMap = new Map(prev);
                    newMap.set(detId, msg.classifiedName);
                    return newMap;
                  });

                  const targets = resolvedTargetNames;
                  if (targets.size > 0 && !targets.has(msg.classifiedName)) {
                    const idx = allDetections.indexOf(det);
                    if (idx !== -1) allDetections.splice(idx, 1);
                    setAllComponentSelections((prev: any) => {
                      const newMap = new Map(prev);
                      const compKey = component.name;
                      const currentSet = newMap.get(compKey) as Set<string> | undefined;
                      if (currentSet) {
                        currentSet.delete(detId);
                        newMap.set(compKey, currentSet);
                      }
                      return newMap;
                    });
                  }
                }
                setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));

              } else if (msg.event === 'remove') {
                const removals = msg.removals || [];
                if (removals.length > 0) {
                  const removalSet: Set<string> = new Set(removals.map((r: any) =>
                    `unfiltered-${r.pageNum}-${r.relX.toFixed(5)}-${r.relY.toFixed(5)}`
                  ));

                  for (let i = allDetections.length - 1; i >= 0; i--) {
                    if (removalSet.has(allDetections[i].id)) {
                      allDetections.splice(i, 1);
                    }
                  }

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = newMap.get(compKey) as Set<string> | undefined;
                    if (currentSet) {
                      removalSet.forEach((id: string) => currentSet.delete(id));
                      newMap.set(compKey, currentSet);
                    }
                    return newMap;
                  });

                  console.log(`🗑️ Removed ${removals.length} non-matching detections (remaining: ${allDetections.length})`);
                  await new Promise(resolve => setTimeout(resolve, 0));
                }

              } else if (msg.event === 'batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                console.log(`✅ Page ${batchPageNum}: classified batch arrived`);

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} final detections`);

              } else if (msg.event === 'done') {
                receivedDone = true;
                const defaultClassName = receptacleRefTypes.length > 0 ? receptacleRefTypes[0].name : (isReceptaclesGroup ? 'Receptacle' : component.name);
                for (const det of allDetections) {
                  if (det.prediction.unfiltered) {
                    det.prediction.unfiltered = false;
                  }
                  if (isReceptaclesGroup && !det.prediction.classifiedName) {
                    det.prediction.classifiedName = defaultClassName;
                    setDetectionClassifications((prev: any) => {
                      const newMap = new Map(prev);
                      if (!newMap.has(det.id)) {
                        newMap.set(det.id, defaultClassName);
                      }
                      return newMap;
                    });
                  }
                }

                const targets = resolvedTargetNames;
                if (targets.size > 0) {
                  const removedIds: string[] = [];
                  for (let i = allDetections.length - 1; i >= 0; i--) {
                    const cls = allDetections[i].prediction.classifiedName || defaultClassName;
                    if (!targets.has(cls)) {
                      removedIds.push(allDetections[i].id);
                      allDetections.splice(i, 1);
                    }
                  }
                  if (removedIds.length > 0) {
                    setAllComponentSelections((prev: any) => {
                      const newMap = new Map(prev);
                      const compKey = component.name;
                      const currentSet = newMap.get(compKey) as Set<string> | undefined;
                      if (currentSet) {
                        removedIds.forEach(id => currentSet.delete(id));
                        newMap.set(compKey, currentSet);
                      }
                      return newMap;
                    });
                    console.log(`🎯 Target filter removed ${removedIds.length} non-matching detections at done (remaining: ${allDetections.length})`);
                  }
                }

                setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                const displayNameDone = isReceptaclesGroup ? 'RECEPTACLES' : component.name;
                setCountAllProgress(`Found ${allDetections.length} ${displayNameDone} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                const serverErrorMessage = String(msg.message || 'Unknown receptacles server error');
                console.error(`❌ Server error:`, serverErrorMessage);
                if (isNoResultMessage(serverErrorMessage)) {
                  throw new Error(serverErrorMessage);
                }
              }
            } catch (parseErr) {
              const parseErrorMessage = parseErr instanceof Error ? parseErr.message : String(parseErr);
              if (isNoResultMessage(parseErrorMessage)) {
                throw parseErr;
              }
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        const isComplete = isAcceptableStreamCompletion(
          receivedDone,
          completedPageCount,
          pageNumbers.length,
          displayName
        );
        if (!isComplete) {
          throw new Error(`Incomplete stream: ${completedPageCount}/${pageNumbers.length} pages, done=${receivedDone}`);
        }

        console.log(`🔢 Total detections from server: ${allDetections.length}`);

        reviseEstimateIfNeeded(detectionsForEstimate || allDetections.length);
        setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side counting "${component.name}"`);
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoResultFailure = isNoResultMessage(errorMessage);
        console.error(`❌ Receptacles stream attempt ${_attempt + 1} failed:`, error);
        if (isNoResultFailure && _attempt < maxPowerNoResultRetries) {
          const retryLabel = `${_attempt + 1}/${maxPowerNoResultRetries}`;
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`No results found for "${displayName}". Retrying (${retryLabel})...`);
          console.log(`🔄 Auto-retrying receptacles stream after no-result response (${retryLabel})...`);
          continue;
        }
        if (!isNoResultFailure && _attempt < maxPowerTrafficRetries) {
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`Request failed due to high traffic. Retrying "${displayName}"...`);
          console.log(`🔄 Auto-retrying receptacles stream...`);
          continue;
        }
        markComponentFailed(isNoResultFailure ? 'No results found. Try again.' : 'Request failed due to high traffic. Try again.');
        finalizeComponentCount();
        return;
      }
      }
    }

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Tele/Data outlets
    // ============================================================
    if (isTeleDataComponent && file?.url) {
      const displayName = isTeleDataGroup ? 'TELE/DATA OUTLETS' : component.name;
      console.log(`📡 CLOUD RUN: Counting "${displayName}" on pages [${pageNumbers.join(', ')}] — tele/data detection`);
      setCountAllProgress(`Counting "${displayName}" across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      const TELE_DATA_MODAL_URL = 'https://joules--tele-data-multipage-pipeline-process.modal.run';
      const requestPayload = {
        pdfUrl: file.url,
        ...(Array.isArray(pageNumbers) && pageNumbers.length > 0
          ? { pageNumbers }
          : { pageNum: pageNumber }),
        componentType: component.name,
        confidence: 0.01,
      };

      for (let _attempt = 0; _attempt < maxPowerNoResultRetries + 1; _attempt++) {
      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;
        let receivedDone = false;
        const attemptToken = _attempt === 0 ? runToken : crypto.randomUUID();
        streamRunTokensRef.current.set(componentIndex, attemptToken);

        const response = await fetch(TELE_DATA_MODAL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify(requestPayload)
        });

        if (!response.ok || !response.body) {
          throw new Error(`Tele-data request failed: ${response.status} ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            if (streamRunTokensRef.current.get(componentIndex) !== attemptToken) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  const nextPct = Math.max(0, Math.min(100, Number(msg.pct) || 0));
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex
                      ? { ...p, completedBatches: Math.max(p.completedBatches || 0, nextPct), totalBatches: 100 }
                      : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `teledata-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      classifiedName: pred.classificationTop || undefined,
                      classificationConfidence: pred.classificationConfidence,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                    newDetections.forEach((d: any) => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} tele/data detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} final detections`);

              } else if (msg.event === 'done') {
                receivedDone = true;
                const defaultClassName = isTeleDataGroup && resolvedTeleDataGroupComponents.length > 0
                  ? resolvedTeleDataGroupComponents[0].name
                  : component.name;
                for (const det of allDetections) {
                  if (!det.prediction.classifiedName) {
                    det.prediction.classifiedName = defaultClassName;
                    setDetectionClassifications((prev: any) => {
                      const newMap = new Map(prev);
                      if (!newMap.has(det.id)) {
                        newMap.set(det.id, defaultClassName);
                      }
                      return newMap;
                    });
                  }
                }

                const targets = resolvedTargetNames;
                // if (targets.size > 0) {
                //   const removedIds: string[] = [];
                //   for (let i = allDetections.length - 1; i >= 0; i--) {
                //     const cls = allDetections[i].prediction.classifiedName || defaultClassName;
                //     if (!targets.has(cls)) {
                //       removedIds.push(allDetections[i].id);
                //       allDetections.splice(i, 1);
                //     }
                //   }
                //   if (removedIds.length > 0) {
                //     setAllComponentSelections((prev: any) => {
                //       const newMap = new Map(prev);
                //       const compKey = component.name;
                //       const currentSet = newMap.get(compKey) as Set<string> | undefined;
                //       if (currentSet) {
                //         removedIds.forEach(id => currentSet.delete(id));
                //         newMap.set(compKey, currentSet);
                //       }
                //       return newMap;
                //     });
                //     console.log(`🎯 Target filter removed ${removedIds.length} non-matching tele/data detections at done (remaining: ${allDetections.length})`);
                //   }
                // }

                setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                setCountAllProgress(`Found ${allDetections.length} ${displayName} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                const serverErrorMessage = String(msg.message || 'Unknown tele/data server error');
                console.error(`❌ Server error:`, serverErrorMessage);
                if (isNoResultMessage(serverErrorMessage)) {
                  throw new Error(serverErrorMessage);
                }
              }
            } catch (parseErr) {
              const parseErrorMessage = parseErr instanceof Error ? parseErr.message : String(parseErr);
              if (isNoResultMessage(parseErrorMessage)) {
                throw parseErr;
              }
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        const isComplete = isAcceptableStreamCompletion(
          receivedDone,
          completedPageCount,
          pageNumbers.length,
          displayName
        );
        if (!isComplete) {
          throw new Error(`Incomplete stream: ${completedPageCount}/${pageNumbers.length} pages, done=${receivedDone}`);
        }

        console.log(`🔢 Total tele/data detections from server: ${allDetections.length}`);

        reviseEstimateIfNeeded(allDetections.length);
        setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side counting "${component.name}"`);
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoResultFailure = isNoResultMessage(errorMessage);
        console.error(`❌ Tele/data stream attempt ${_attempt + 1} failed:`, error);
        if (isNoResultFailure && _attempt < maxPowerNoResultRetries) {
          const retryLabel = `${_attempt + 1}/${maxPowerNoResultRetries}`;
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`No results found for "${displayName}". Retrying (${retryLabel})...`);
          console.log(`🔄 Auto-retrying tele/data stream after no-result response (${retryLabel})...`);
          continue;
        }
        if (!isNoResultFailure && _attempt < maxPowerTrafficRetries) {
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`Request failed due to high traffic. Retrying "${displayName}"...`);
          console.log(`🔄 Auto-retrying tele/data stream...`);
          continue;
        }
        markComponentFailed(isNoResultFailure ? 'No results found. Try again.' : 'Request failed due to high traffic. Try again.');
        finalizeComponentCount();
        return;
      }
      }
    }

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Lighting
    // ============================================================
    if ((isLightingComponent || isLightingGroup) && file?.url) {
      const displayName = isLightingGroup ? 'LIGHTING' : component.name;

      for (let noFixtureCodeRetry = 0; noFixtureCodeRetry <= maxNoResultRetries; noFixtureCodeRetry++) {
        const lightingCompsForRequest = isLightingGroup ? resolvedLightingGroupComponents : [component];
        const fixtureCodes = lightingCompsForRequest
          .map((c: any) => (c.symbol || c.name || '').trim().toUpperCase())
          .filter((s: string) => s.length > 0 && s !== 'LIGHTING' && s !== 'LIGHTING FIXTURE' && s !== 'LIGHTING FIXTURES');

        console.log(`💡 CLOUD RUN: Counting "${displayName}" (${fixtureCodes.length} codes: [${fixtureCodes.join(', ')}]) on pages [${pageNumbers.join(', ')}]`);
        if (fixtureCodes.length === 0) {
          if (noFixtureCodeRetry < maxNoResultRetries) {
            const retryLabel = `${noFixtureCodeRetry + 1}/${maxNoResultRetries}`;
            console.warn(`⚠️ No valid fixture codes found for lighting count, retrying (${retryLabel})...`);
            setCountAllProgress(`No valid lighting fixture codes found in key. Retrying (${retryLabel})...`);
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          console.warn('⚠️ No valid fixture codes found for lighting count');
          setCountAllProgress('No valid lighting fixture codes found in key.');
          markComponentFailed('No valid lighting fixture codes found in key.');
          finalizeComponentCount();
          return;
        }
        setCountAllProgress(`Counting ${displayName} across ${pageCount} pages...`);

        const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
        const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
        const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

        try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];

        const allFixtureCodes = (electricalKey?.components || [])
          .filter((c: any) => c.symbolType === 'Lighting')
          .map((c: any) => (c.symbol || c.name || '').trim().toUpperCase())
          .filter((s: string) => s.length > 0);

        const completedPageSet = new Set<number>();
        const markLightingPageDone = (pageNum: number): number => {
          completedPageSet.add(pageNum);
          return completedPageSet.size;
        };

        const CLOUD_RUN_URL = 'https://autocount-lighting-904918874970.us-central1.run.app';

        const processPageStream = async (pg: number) => {
          const response = await fetch(CLOUD_RUN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfUrl: file.url,
              pageNumbers: [pg],
              fixtureCodes,
              fixtureCode: fixtureCodes[0],
              allFixtureCodes
            })
          });

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let streamBuffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            streamBuffer += decoder.decode(value, { stream: true });
            const events = streamBuffer.split('\n\n');
            streamBuffer = events.pop() || '';

            for (const eventBlock of events) {
              if (!eventBlock.trim()) continue;
              const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
              if (!dataLine) continue;
              try {
                const msg = JSON.parse(dataLine.slice(6));

                if (msg.event === 'progress') {
                  const progressPageNum = msg.pageNum || pg;
                  const phase = msg.phase || 'scanning';
                  const pct = msg.pct || 0;
                  const unfilteredTotal = msg.unfilteredTotal || 0;

                  setCountAllProgress(`Scanning for lighting fixtures... (${allDetections.length} found)`);
                  setLightingProgress((prev: any) => {
                    const newPageProgress = new Map(prev.pageProgress);

                    if (phase === 'verifying') {
                      newPageProgress.forEach((status: any, pgNum: unknown) => {
                        if (status.phase !== 'done') {
                          newPageProgress.set(pgNum as number, {
                            ...status,
                            phase: 'verifying',
                            pct: Math.max(status.pct || 0, 88)
                          });
                        }
                      });
                    }

                    const existing = (newPageProgress.get(progressPageNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 }) as any;
                    if (existing.phase === 'done') {
                      return prev;
                    }
                    newPageProgress.set(progressPageNum, { ...existing, phase, pct: Math.max(existing.pct || 0, pct) });

                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                      totalCandidates: unfilteredTotal || prev.totalCandidates,
                      message: phase === 'rendering' ? `Rendering page ${progressPageNum}...`
                        : phase === 'verifying' ? `Verifying ${unfilteredTotal || prev.totalCandidates} candidates with AI...`
                        : `Scanning page ${progressPageNum}...`
                    };
                  });

                } else if (msg.event === 'page-progress') {
                  const pageProgressNum = msg.pageNum || pg;
                  const phase = msg.phase || 'scanning';
                  const pct = msg.pct || 0;
                  if (msg.completedTiles != null) {
                    setLightingProgress((prev: any) => {
                      const newPageProgress = new Map(prev.pageProgress);
                      const existing = (newPageProgress.get(pageProgressNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 }) as any;
                      if (existing.phase === 'done') {
                        return prev;
                      }

                      const newPhase = (existing.phase === 'verifying' && phase === 'scanning') ? 'verifying' : phase;
                      const newPct = Math.max(existing.pct || 0, pct);

                      newPageProgress.set(pageProgressNum, {
                        ...existing,
                        phase: newPhase,
                        pct: newPct,
                        completedTiles: msg.completedTiles,
                        totalTiles: msg.totalTiles
                      });
                      return {
                        ...prev,
                        pageProgress: newPageProgress,
                        overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                        message: newPhase === 'verifying' ? `Verifying page ${pageProgressNum} with AI...` : `Scanning page ${pageProgressNum}... (${msg.completedTiles}/${msg.totalTiles} tiles)`
                      };
                    });
                  }

                } else if (msg.event === 'unfiltered-batch') {
                  const batchPageNum = msg.pageNum || pg;
                  const batchFixtureCode = msg.fixtureCode || '';
                  const unfilteredCount = (msg.newPredictions || []).length;
                  setCountAllProgress(`Scanning for lighting fixtures... detecting on page ${batchPageNum}... (verifying with AI before showing)`);
                  setLightingProgress((prev: any) => ({
                    ...prev,
                    message: `Detecting on page ${batchPageNum}... verifying with AI`
                  }));
                  console.log(`📥 Page ${batchPageNum} [${batchFixtureCode}]: +${unfilteredCount} unfiltered (held for verification)`);

                } else if (msg.event === 'verified-batch') {
                  const verifiedCode = msg.fixtureCode || '';
                  const verifiedPreds = msg.predictions || [];
                  const verifiedSoFar = msg.verifiedSoFar || 0;
                  const totalToVerify = msg.totalToVerify || 0;

                  const targets = resolvedTargetNames;
                  if (targets.size > 0 && verifiedCode && !targets.has(verifiedCode) && !targets.has(verifiedCode.toUpperCase())) {
                    console.log(`🎯 Skipping ${verifiedPreds.length} lighting detections for "${verifiedCode}" (not in target set)`);
                  } else {

                  const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                  for (const pred of verifiedPreds) {
                    const screenX = pred.relX * onScreenWidth;
                    const screenY = pred.relY * onScreenHeight;
                    const screenWidth = pred.relWidth * onScreenWidth;
                    const screenHeight = pred.relHeight * onScreenHeight;
                    const predPageNum = pred.pageNum || pg;

                    newDetections.push({
                      id: `lighting-${verifiedCode}-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                      thumbnail: pred.thumbnail || '',
                      prediction: {
                        ...pred,
                        x: screenX,
                        y: screenY,
                        width: screenWidth,
                        height: screenHeight,
                        page: predPageNum,
                        classifiedName: verifiedCode,
                      }
                    });
                  }

                  if (newDetections.length > 0) {
                    allDetections.push(...newDetections);

                    setMultiComponentDetections((prev: any) => {
                      const updated = [...prev];
                      const comp = updated[componentIndex];
                      if (comp) {
                        updated[componentIndex] = { ...comp, detections: [...allDetections] };
                      }
                      return updated;
                    });
                    const compKey = isLightingGroup ? '__LIGHTING_GROUP__' : (component.symbol || component.name);
                    setAllComponentSelections((prev: any) => {
                      const newMap = new Map(prev);
                      const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                      newDetections.forEach((d: any) => currentSet.add(d.id));
                      newMap.set(compKey, currentSet);
                      return newMap;
                    });
                    if (isLightingGroup && verifiedCode) {
                      setDetectionClassifications((prev: any) => {
                        const newMap = new Map(prev);
                        newDetections.forEach((d: any) => newMap.set(d.id, verifiedCode));
                        return newMap;
                      });
                    }
                  }

                  setLightingProgress((prev: any) => {
                    const newPageProgress = new Map(prev.pageProgress);

                    const affectedPages = new Set<number>();
                    if (newDetections.length > 0) {
                      newDetections.forEach((d: any) => affectedPages.add(d.prediction.page));
                    } else {
                      affectedPages.add(pg);
                    }

                    affectedPages.forEach((affectedPg: number) => {
                      const pgStatus = (newPageProgress.get(affectedPg) || { phase: 'verifying', pct: 88, completedTiles: 1, totalTiles: 1 }) as any;
                      if (pgStatus.phase !== 'done') {
                        const verifyRatio = totalToVerify > 0 ? verifiedSoFar / totalToVerify : 0;
                        const pagePct = 88 + (verifyRatio * 12);

                        newPageProgress.set(affectedPg, {
                          ...pgStatus,
                          phase: 'verifying',
                          pct: Math.max(pgStatus.pct || 0, pagePct),
                          verifiedSoFar,
                          totalToVerify
                        });
                      }
                    });

                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                      detectionsFound: allDetections.length,
                      verifiedCount: verifiedSoFar,
                      totalCandidates: totalToVerify || prev.totalCandidates,
                      message: `Verified ${allDetections.length} fixtures (${verifiedSoFar}/${totalToVerify || '?'} candidates checked)`
                    };
                  });
                  console.log(`🔍 Verified [${verifiedCode}]: ${verifiedPreds.length} kept (total: ${allDetections.length})`);
                  }
                } else if (msg.event === 'page-scanned') {
                  const scannedPageNum = msg.pageNum || pg;
                  console.log(`📡 Page ${scannedPageNum} scanned: ${msg.totalPredictions} candidates found`);
                  setLightingProgress((prev: any) => {
                    const newPageProgress = new Map(prev.pageProgress);
                    const existing = (newPageProgress.get(scannedPageNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 }) as any;
                    if (existing.phase !== 'done') {
                      newPageProgress.set(scannedPageNum, { ...existing, phase: 'verifying', pct: 88 });
                    }
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                      message: `Page ${scannedPageNum} scanned. Verifying ${msg.totalPredictions} candidates...`
                    };
                  });

                } else if (msg.event === 'page-done') {
                  const donePageNum = msg.pageNum || pg;
                  const completedPageCount = markLightingPageDone(donePageNum);
                  console.log(`✅ Page ${donePageNum} fully done: ${msg.totalPredictions} lighting detections (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllProgress(`Scanning for lighting fixtures... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));
                  setLightingProgress((prev: any) => {
                    const newPageProgress = new Map(prev.pageProgress);
                    newPageProgress.set(donePageNum, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                      detectionsFound: allDetections.length,
                      message: `${completedPageCount}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
                    };
                  });

                } else if (msg.event === 'done') {
                  const completedPageCount = markLightingPageDone(pg);
                  setCountAllProgress(`Finalizing lighting fixtures... (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));
                  setLightingProgress((prev: any) => {
                    const newPageProgress = new Map(prev.pageProgress);
                    newPageProgress.set(pg, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress as any, prev.totalPages),
                      detectionsFound: allDetections.length,
                      message: `${completedPageCount}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
                    };
                  });

                } else if (msg.event === 'error') {
                  const serverErrorMessage = String(msg.message || 'Unknown lighting server error');
                  console.error(`❌ Page ${pg} server error:`, serverErrorMessage);
                }
              } catch (parseErr) {
                console.warn(`⚠️ Failed to parse lighting SSE event for page ${pg}:`, parseErr);
              }
            }
          }
        };

        const PAGE_REQUEST_CONCURRENCY = 2;

        console.log(`🚀 Launching ${pageNumbers.length} page requests (2 at a time)`);
        for (let i = 0; i < pageNumbers.length; i += PAGE_REQUEST_CONCURRENCY) {
          const pageBatch = pageNumbers.slice(i, i + PAGE_REQUEST_CONCURRENCY);
          const batchResults = await Promise.allSettled(pageBatch.map((pg: number) => processPageStream(pg)));
          const failures = batchResults.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
          if (failures.length > 0) {
            const reason = failures
              .map((failure) => String((failure.reason && failure.reason.message) || failure.reason || 'unknown error'))
              .join(' | ');
            throw new Error(`Lighting page batch failed: ${reason}`);
          }
        }

        if (allDetections.length === 0) {
          if (noFixtureCodeRetry < maxNoResultRetries) {
            const retryLabel = `${noFixtureCodeRetry + 1}/${maxNoResultRetries}`;
            console.warn(`⚠️ Lighting count finished with 0 detections. Retrying full pass (${retryLabel})...`);
            setCountAllProgress(`No results found for "${displayName}". Retrying (${retryLabel})...`);
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          console.warn(`⚠️ Lighting count finished with 0 detections after ${maxNoResultRetries + 1} attempts.`);
          setCountAllProgress(`No results found for "${displayName}".`);
          markComponentFailed('No results found after retries. Try again.');
          finalizeComponentCount();
          return;
        }

        for (const det of allDetections) {
          if (det.prediction.unfiltered) det.prediction.unfiltered = false;
        }

        setMultiComponentDetections((prev: any) => {
          const updated = [...prev];
          const comp = updated[componentIndex];
          if (comp) {
            updated[componentIndex] = { ...comp, detections: [...allDetections] };
          }
          return updated;
        });

        setLightingProgress((prev: any) => {
          const newPageProgress = new Map(prev.pageProgress);
          pageNumbers.forEach((pageNum: number) => {
            newPageProgress.set(pageNum, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
          });
          return {
            ...prev,
            pageProgress: newPageProgress,
            overallPct: 100,
            detectionsFound: allDetections.length,
            message: `${pageNumbers.length}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
          };
        });
        setCountAllProgress(`Found ${allDetections.length} lighting fixtures across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total detections`);

        console.log(`🔢 Total lighting detections from server: ${allDetections.length}`);

        await new Promise(resolve => setTimeout(resolve, 300));

          reviseEstimateIfNeeded(allDetections.length);
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
          ));
          finalizeComponentCount();
          console.log(`✅ Finished server-side counting "${displayName}"`);
          return;
        } catch (error) {
          console.error(`❌ Lighting server-side batch failed:`, error);
          setCountAllProgress(`Server-side failed for "${displayName}".`);
          setLightingProgress((prev: any) => ({ ...prev, message: 'Server-side failed. Please try again.' }));
          markComponentFailed('Server-side failed. Please try again.');
          finalizeComponentCount();
          return;
        }
      }
      return;
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Switches
    // ============================================================
    if (isSwitchComponent && file?.url) {
      const switchDisplayName = component.name;

      console.log(`🔌 CLOUD RUN: Counting switch "${switchDisplayName}" on pages [${pageNumbers.join(', ')}]`);
      setCountAllProgress(`Counting ${switchDisplayName} across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      const SWITCH_MODAL_URL = 'https://joules--switches-multipage-pipeline-process.modal.run';

      for (let _attempt = 0; _attempt < maxPowerNoResultRetries + 1; _attempt++) {
      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;
        let receivedDone = false;
        const attemptToken = _attempt === 0 ? runToken : crypto.randomUUID();
        streamRunTokensRef.current.set(componentIndex, attemptToken);

        const response = await fetch(SWITCH_MODAL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            componentType: 'switch',
            confidence: 0.01
          })
        });

        if (!response.ok || !response.body) {
          throw new Error(`Switch request failed: ${response.status} ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
            if (!dataLine) continue;
            if (streamRunTokensRef.current.get(componentIndex) !== attemptToken) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || `Scanning for switches... (${allDetections.length} found)`);
                if (msg.pct != null) {
                  const nextPct = Math.max(0, Math.min(100, Number(msg.pct) || 0));
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex
                      ? { ...p, completedBatches: Math.max(p.completedBatches || 0, nextPct), totalBatches: 100 }
                      : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                const pageNum = msg.pageNum || pageNumber;
                const message = msg.message || `Page ${pageNum}: processing...`;
                setCountAllProgress(`${message} (${allDetections.length} found)`);
                setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) => {
                  if (idx !== componentIndex) return p;
                  const completedTiles = typeof msg.completedTiles === 'number' ? msg.completedTiles : undefined;
                  const totalTiles = typeof msg.totalTiles === 'number' ? msg.totalTiles : undefined;
                  let fallbackPct = p.completedBatches || 0;
                  if (completedTiles !== undefined && totalTiles && totalTiles > 0) {
                    const currentPagePct = (completedTiles / totalTiles) * 100;
                    const aggregatePct = Math.round(((completedPageCount + (currentPagePct / 100)) / pageNumbers.length) * 100);
                    fallbackPct = Math.max(fallbackPct, Math.min(99, aggregatePct));
                  }
                  return {
                    ...p,
                    currentPage: pageNum,
                    currentBatch: completedTiles ?? p.currentBatch,
                    completedBatches: fallbackPct,
                    totalBatches: 100
                  };
                }));

              } else if (msg.event === 'unfiltered-batch') {
                const batchPreds = msg.newPredictions || [];
                const batchPageNum = msg.pageNum || pageNumber;

                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of batchPreds) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;
                  const predPageNum = pred.pageNum || batchPageNum;

                  newDetections.push({
                    id: `switch-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: predPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  const compKey = component.name;
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                    newDetections.forEach((d: any) => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${batchPreds.length} detections (total: ${allDetections.length})`);
                setCountAllProgress(`Scanning for switches... (${allDetections.length} found)`);

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} switch detections (${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllProgress(`Scanning for switches... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                  idx === componentIndex
                    ? {
                        ...p,
                        completedBatches: Math.max(p.completedBatches || 0, Math.round((completedPageCount / pageNumbers.length) * 100)),
                        totalBatches: 100
                      }
                    : p
                ));

              } else if (msg.event === 'done') {
                receivedDone = true;

              } else if (msg.event === 'error') {
                const serverErrorMessage = String(msg.message || 'Unknown switch server error');
                console.error(`❌ Switch server error:`, serverErrorMessage);
                if (isNoResultMessage(serverErrorMessage)) {
                  throw new Error(serverErrorMessage);
                }
              }
            } catch (parseErr) {
              const parseErrorMessage = parseErr instanceof Error ? parseErr.message : String(parseErr);
              if (isNoResultMessage(parseErrorMessage)) {
                throw parseErr;
              }
              // skip unparseable event
            }
          }
        }

        const isComplete = isAcceptableStreamCompletion(
          receivedDone,
          completedPageCount,
          pageNumbers.length,
          switchDisplayName
        );
        if (!isComplete) {
          throw new Error(`Incomplete stream: ${completedPageCount}/${pageNumbers.length} pages, done=${receivedDone}`);
        }

        setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
          idx === componentIndex ? { ...d, detections: [...allDetections] } : d
        ));
        setCountAllProgress(`Found ${allDetections.length} switches across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total switch detections`);

        reviseEstimateIfNeeded(allDetections.length);
        setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side switch counting "${switchDisplayName}"`);
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoResultFailure = isNoResultMessage(errorMessage);
        console.error(`❌ Switches stream attempt ${_attempt + 1} failed:`, error);
        if (isNoResultFailure && _attempt < maxPowerNoResultRetries) {
          const retryLabel = `${_attempt + 1}/${maxPowerNoResultRetries}`;
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`No results found for "${switchDisplayName}". Retrying (${retryLabel})...`);
          console.log(`🔄 Auto-retrying switches stream after no-result response (${retryLabel})...`);
          continue;
        }
        if (!isNoResultFailure && _attempt < maxPowerTrafficRetries) {
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`Request failed due to high traffic. Retrying "${switchDisplayName}"...`);
          console.log(`🔄 Auto-retrying switches stream...`);
          continue;
        }
        markComponentFailed(isNoResultFailure ? 'No results found. Try again.' : 'Request failed due to high traffic. Try again.');
        finalizeComponentCount();
        return;
      }
      }
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Junction Boxes
    // ============================================================
    if (isJunctionBoxComponent && file?.url) {
      const jboxDisplayName = component.name;

      console.log(`📦 CLOUD RUN: Counting junction box "${jboxDisplayName}" on pages [${pageNumbers.join(', ')}]`);
      setCountAllProgress(`Counting ${jboxDisplayName} across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      const JBOX_MODAL_URL = 'https://joules--jboxes-multipage-pipeline-process.modal.run';

      for (let _attempt = 0; _attempt < maxPowerNoResultRetries + 1; _attempt++) {
      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;
        let receivedDone = false;
        const attemptToken = _attempt === 0 ? runToken : crypto.randomUUID();
        streamRunTokensRef.current.set(componentIndex, attemptToken);

        const response = await fetch(JBOX_MODAL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            componentType: 'junction box',
            confidence: 0.01
          })
        });

        if (!response.ok || !response.body) {
          throw new Error(`JBox request failed: ${response.status} ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
            if (!dataLine) continue;
            if (streamRunTokensRef.current.get(componentIndex) !== attemptToken) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || `Scanning for junction boxes... (${allDetections.length} found)`);
                if (msg.pct != null) {
                  const nextPct = Math.max(0, Math.min(100, Number(msg.pct) || 0));
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex
                      ? { ...p, completedBatches: Math.max(p.completedBatches || 0, nextPct), totalBatches: 100 }
                      : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                const pageNum = msg.pageNum || pageNumber;
                const message = msg.message || `Page ${pageNum}: processing...`;
                setCountAllProgress(`${message} (${allDetections.length} found)`);
                setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) => {
                  if (idx !== componentIndex) return p;
                  const completedTiles = typeof msg.completedTiles === 'number' ? msg.completedTiles : undefined;
                  const totalTiles = typeof msg.totalTiles === 'number' ? msg.totalTiles : undefined;
                  let fallbackPct = p.completedBatches || 0;
                  if (completedTiles !== undefined && totalTiles && totalTiles > 0) {
                    const currentPagePct = (completedTiles / totalTiles) * 100;
                    const aggregatePct = Math.round(((completedPageCount + (currentPagePct / 100)) / pageNumbers.length) * 100);
                    fallbackPct = Math.max(fallbackPct, Math.min(99, aggregatePct));
                  }
                  return {
                    ...p,
                    currentPage: pageNum,
                    currentBatch: completedTiles ?? p.currentBatch,
                    completedBatches: fallbackPct,
                    totalBatches: 100
                  };
                }));

              } else if (msg.event === 'unfiltered-batch') {
                const batchPreds = msg.newPredictions || [];
                const batchPageNum = msg.pageNum || pageNumber;

                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of batchPreds) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;
                  const predPageNum = pred.pageNum || batchPageNum;

                  newDetections.push({
                    id: `jbox-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: predPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  const compKey = component.name;
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                    newDetections.forEach((d: any) => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${batchPreds.length} jbox detections (total: ${allDetections.length})`);
                setCountAllProgress(`Scanning for junction boxes... (${allDetections.length} found)`);

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} jbox detections (${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllProgress(`Scanning for junction boxes... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                  idx === componentIndex
                    ? {
                        ...p,
                        completedBatches: Math.max(p.completedBatches || 0, Math.round((completedPageCount / pageNumbers.length) * 100)),
                        totalBatches: 100
                      }
                    : p
                ));

              } else if (msg.event === 'done') {
                receivedDone = true;

              } else if (msg.event === 'error') {
                const serverErrorMessage = String(msg.message || 'Unknown jbox server error');
                console.error(`❌ JBox server error:`, serverErrorMessage);
                if (isNoResultMessage(serverErrorMessage)) {
                  throw new Error(serverErrorMessage);
                }
              }
            } catch (parseErr) {
              const parseErrorMessage = parseErr instanceof Error ? parseErr.message : String(parseErr);
              if (isNoResultMessage(parseErrorMessage)) {
                throw parseErr;
              }
              // skip unparseable event
            }
          }
        }

        const isComplete = isAcceptableStreamCompletion(
          receivedDone,
          completedPageCount,
          pageNumbers.length,
          jboxDisplayName
        );
        if (!isComplete) {
          throw new Error(`Incomplete stream: ${completedPageCount}/${pageNumbers.length} pages, done=${receivedDone}`);
        }

        setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
          idx === componentIndex ? { ...d, detections: [...allDetections] } : d
        ));
        setCountAllProgress(`Found ${allDetections.length} junction boxes across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total jbox detections`);

        reviseEstimateIfNeeded(allDetections.length);
        setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side junction box counting "${jboxDisplayName}"`);
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoResultFailure = isNoResultMessage(errorMessage);
        console.error(`❌ JBox stream attempt ${_attempt + 1} failed:`, error);
        if (isNoResultFailure && _attempt < maxPowerNoResultRetries) {
          const retryLabel = `${_attempt + 1}/${maxPowerNoResultRetries}`;
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`No results found for "${jboxDisplayName}". Retrying (${retryLabel})...`);
          console.log(`🔄 Auto-retrying jbox stream after no-result response (${retryLabel})...`);
          continue;
        }
        if (!isNoResultFailure && _attempt < maxPowerTrafficRetries) {
          markComponentFailed('');
          setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
            i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
          ));
          setCountAllProgress(`Request failed due to high traffic. Retrying "${jboxDisplayName}"...`);
          console.log(`🔄 Auto-retrying jbox stream...`);
          continue;
        }
        markComponentFailed(isNoResultFailure ? 'No results found. Try again.' : 'Request failed due to high traffic. Try again.');
        finalizeComponentCount();
        return;
      }
      }
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Generic Gemini Vision
    // ============================================================
    const isGenericComponent = !isLightingComponent && !isLightingGroup && !isSwitchComponent && !isReceptacleComponent && !isReceptaclesGroup && !isTeleDataComponent && !isJunctionBoxComponent;
    if (isGenericComponent && file?.url && component.referenceImage) {
      console.log(`🔮 CLOUD RUN: Counting "${component.name}" on pages [${pageNumbers.join(', ')}] — Gemini vision detection`);
      setCountAllProgress(`Counting "${component.name}" across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      for (let _attempt = 0; _attempt < maxPowerNoResultRetries + 1; _attempt++) {
      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;

        const response = await fetch('https://autocount-gemini-904918874970.us-central1.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            referenceAreaBase64: component.referenceImage,
            componentType: component.name,
            componentSymbol: component.symbol || component.name
          })
        });

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  const nextPct = Math.max(0, Math.min(100, Number(msg.pct) || 0));
                  setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                    idx === componentIndex
                      ? { ...p, completedBatches: Math.max(p.completedBatches || 0, nextPct), totalBatches: 100 }
                      : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `gemini-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections((prev: any) => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set((newMap.get(compKey) as Iterable<string>) || []);
                    newDetections.forEach((d: any) => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} Gemini detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} detections (${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllProgress(`Scanning for ${component.name}... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllComponentProgress((prev: any) => prev.map((p: any, idx: number) =>
                  idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                ));

              } else if (msg.event === 'done') {
                setMultiComponentDetections((prev: any) => prev.map((d: any, idx: number) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                setCountAllProgress(`Found ${allDetections.length} ${component.name} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                const serverErrorMessage = String(msg.message || 'Unknown generic server error');
                console.error(`❌ Server error:`, serverErrorMessage);
                if (isNoResultMessage(serverErrorMessage)) {
                  throw new Error(serverErrorMessage);
                }
              }
            } catch (parseErr) {
              const parseErrorMessage = parseErr instanceof Error ? parseErr.message : String(parseErr);
              if (isNoResultMessage(parseErrorMessage)) {
                throw parseErr;
              }
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        console.log(`🔢 Total Gemini detections from server: ${allDetections.length}`);

        reviseEstimateIfNeeded(allDetections.length);
        setCountAllComponentProgress((prev: any) => prev.map((p: any, i: number) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side Gemini counting "${component.name}"`);
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoResultFailure = isNoResultMessage(errorMessage);
        console.error(`❌ Gemini server-side batch failed:`, error);
        if (isNoResultFailure && _attempt < maxPowerNoResultRetries) {
          const retryLabel = `${_attempt + 1}/${maxPowerNoResultRetries}`;
          setCountAllProgress(`No results found for "${component.name}". Retrying (${retryLabel})...`);
          console.log(`🔄 Auto-retrying generic stream after no-result response (${retryLabel})...`);
          continue;
        }
        setCountAllProgress(`Server-side failed for "${component.name}".`);
        markComponentFailed(isNoResultFailure ? 'No results found. Try again.' : 'Server-side failed. Please try again.');
        finalizeComponentCount();
        return;
      }
      }
    }
    // ============================================================
    // END SERVER-SIDE BATCH PATH
    // ============================================================
    markComponentFailed('Client-side fallback is disabled. This component must use a server-side counting endpoint.');
    finalizeComponentCount();
    return;
  }, [
    pdfDocument,
    numPages,
    pageNumber,
    canvasRef,
    file,
    lightingGroupComponents,
    teleDataGroupComponents,
    electricalKey,
    streamRunTokensRef,
    retryPayloadsRef,
    countTargetNamesRef,
    setCountAllComponentProgress,
    setMultiComponentDetections,
    setAllComponentSelections,
    setCountAllProgress,
    setIsCountingAll,
    setDetectionClassifications,
    setLightingProgress,
  ]);

  return { countSingleComponent };
}
